"use client";

import { memo, useEffect, useRef, useState } from "react";

import { ActivityLevelGaugeProps } from "../types";

const COLORS = {
  red: "#E03923", // Low score
  orange: "#FFA000", // Medium score
  green: "#228568", // High score
  gaugeBg: "#E2EAF4", // Background arc
  labelBlue: "#475799", // "Activity level" text
};

// Easing function for smooth animation
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const ActivityLevelGauge: React.FC<ActivityLevelGaugeProps> = ({ score }) => {
  const [animatedScore, setAnimatedScore] = useState(0);
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    let startTimestamp: number | null = null;
    const animationDuration = 800;

    const animate = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const elapsed = timestamp - startTimestamp;
      const progress = Math.min(elapsed / animationDuration, 1);
      const easedProgress = easeOutCubic(progress);
      setAnimatedScore(Math.round(score * easedProgress));
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        animationRef.current = null;
      }
    };

    if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    setAnimatedScore(0);
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [score]);

  const getScoreColor = (scoreValue: number) => {
    if (scoreValue < 40) return COLORS.red;
    if (scoreValue < 70) return COLORS.orange;
    return COLORS.green;
  };

  const getScoreLabel = (scoreValue: number) => {
    if (scoreValue < 40) return "Low";
    if (scoreValue < 70) return "Medium";
    return "High";
  };

  const width = 183;
  const height = 92;
  const strokeWidth = 12;
  const radius = (width - strokeWidth) / 2;

  const centerX = width / 2;
  const centerY = height; // Bottom center of the semicircle

  const progress = Math.min(100, Math.max(0, animatedScore));
  const color = getScoreColor(animatedScore);
  const label = getScoreLabel(animatedScore);

  // Calculate arc path for 180 degrees (from left to right, curving up)
  const arcLength = Math.PI * radius;
  const progressOffset = arcLength * (1 - progress / 100);

  // SVG path for semicircle arc (from left to right, going up)
  const describeArc = () => {
    const startX = strokeWidth / 2;
    const startY = height;
    const endX = width - strokeWidth / 2;
    const endY = height;

    return `M ${startX} ${startY} A ${radius} ${radius} 0 0 1 ${endX} ${endY}`;
  };

  // Shadow filter for the arcs
  const shadowFilterId = "gauge-shadow";
  const progressShadowId = "progress-shadow";

  return (
    <div className="flex flex-col items-center">
      {/* Gauge SVG */}
      <div className="relative" style={{ width, height: height + 20 }}>
        <svg
          width={width}
          height={height + 20}
          className="overflow-visible"
          viewBox={`0 0 ${width} ${height + 10}`}
        >
          <defs>
            <filter
              id={shadowFilterId}
              x="-20%"
              y="-20%"
              width="140%"
              height="140%"
            >
              <feDropShadow
                dx="0"
                dy="4"
                stdDeviation="5"
                floodColor="#E2EAF4"
                floodOpacity="0.24"
              />
            </filter>
            <filter
              id={progressShadowId}
              x="-20%"
              y="-20%"
              width="140%"
              height="140%"
            >
              <feDropShadow
                dx="0"
                dy="4"
                stdDeviation="5"
                floodColor={color}
                floodOpacity="0.24"
              />
            </filter>
          </defs>

          <path
            d={describeArc()}
            fill="none"
            stroke={COLORS.gaugeBg}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            filter={`url(#${shadowFilterId})`}
          />

          <path
            d={describeArc()}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={arcLength}
            strokeDashoffset={progressOffset}
            filter={`url(#${progressShadowId})`}
            className="transition-[stroke-dashoffset] duration-500 ease-out"
          />
        </svg>

        <div className="absolute bottom-[10px] left-1/2 flex -translate-x-1/2 flex-col items-center justify-end">
          <div
            className="text-center text-2xl font-bold leading-9"
            style={{ color: color }}
          >
            {label}
          </div>
          <div className="text-center text-xs font-normal leading-4 text-[#475799]">
            Activity level
          </div>
        </div>
      </div>

      <div className="mt-5 max-w-[248px] text-center text-xs font-medium leading-4 text-[#444255]">
        High activity level gets you more attention from recruiters
      </div>
    </div>
  );
};

export default memo(ActivityLevelGauge);
